import * as React from "react";
import { cn } from "../lib/utils";

/**
 * Usage bar - one figure (used / limit) plus a coloured progress bar. Shared between
 * web_admin (Media, MediaPicker, the Usage page) and web_account (the Account page).
 * DISPLAY ONLY. The colour follows the ratio: under 80% primary, 80% or more warning,
 * 100% or more destructive. `unlimited` (limit -1) shows "∞" and mutes the bar.
 *
 * `used`/`limit` are raw values in whatever unit applies (bytes, pages, …). `format` turns
 * them into display text (`formatBytes`, say). `ratio`/`warn`/`exceeded`/`unlimited` may
 * come from the server; without them they are derived from `used`/`limit`.
 */
export interface UsageBarProps {
  label: string;
  used: number;
  limit: number;
  unlimited?: boolean;
  ratio?: number;
  warn?: boolean;
  exceeded?: boolean;
  /** Raw value -> display text (a localized number by default). */
  format?: (n: number) => string;
  /** An extra note after the right-hand text (e.g. "3 files"). */
  hint?: React.ReactNode;
  /** The compact, single-line layout - MediaPicker and other tight spots. */
  compact?: boolean;
  className?: string;
}

const fmtNum = (n: number) => n.toLocaleString();

export function UsageBar({
  label, used, limit, unlimited, ratio, warn, exceeded, format = fmtNum, hint, compact, className,
}: UsageBarProps) {
  const isUnlimited = unlimited ?? limit < 0;
  const r = ratio ?? (isUnlimited ? 0 : limit > 0 ? used / limit : used > 0 ? 1 : 0);
  const isExceeded = exceeded ?? (!isUnlimited && (limit > 0 ? used >= limit : used > 0));
  const isWarn = warn ?? (!isUnlimited && r >= 0.8);
  const pct = isUnlimited ? 0 : Math.min(100, Math.max(0, Math.round(r * 100)));

  const fill = isExceeded ? "bg-destructive" : isWarn ? "bg-warning" : "bg-primary";
  const valueText = `${format(used)} / ${isUnlimited ? "∞" : format(limit)}`;

  if (compact) {
    return (
      // ⚠ `shrink-0` on the container, not only on the children. Every child already
      // refuses to shrink, so without it flex squeezes the BOX below its content width and
      // the text spills out over whatever sits next to it in the toolbar - which looks
      // exactly like a z-index bug and is not one.
      <div className={cn("flex shrink-0 items-center gap-2 text-xs", className)}>
        <span className="shrink-0 text-muted-foreground">{label}</span>
        <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
          {!isUnlimited && <div className={cn("h-full rounded-full transition-all", fill)} style={{ width: `${pct}%` }} />}
        </div>
        <span className={cn("shrink-0 tabular-nums", isExceeded ? "text-destructive" : "text-muted-foreground")}>{valueText}</span>
      </div>
    );
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <span className={cn("text-sm tabular-nums", isExceeded ? "font-semibold text-destructive" : "text-muted-foreground")}>
          {valueText}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        {!isUnlimited && <div className={cn("h-full rounded-full transition-all", fill)} style={{ width: `${pct}%` }} />}
      </div>
      {(hint || !isUnlimited) && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{hint}</span>
          {!isUnlimited && <span className="tabular-nums">{pct}%</span>}
        </div>
      )}
    </div>
  );
}
